/* 
  AccessRegistry.ts
  - Singleton registry that stores raw backend access payload and compiled Policy
  - Pub/sub mechanism for UI to react to updates
*/

import { compilePolicy, type Policy, type RawAccess } from "./AccessPolicy";

class AccessRegistry {
  private raw: RawAccess = {};
  private policy: Policy = compilePolicy({});

  private listeners = new Set<() => void>();

  setRawAccess(raw: RawAccess | null | undefined) {
    this.raw = raw ?? {};
    this.policy = compilePolicy(this.raw);
    this.emit();
  }

  /**
   * Clears the access registry, resetting to default deny-all policy.
   * Call this on logout to ensure stale permissions don't persist.
   */
  clear() {
    this.raw = {};
    this.policy = compilePolicy({});
    this.emit();
  }

  getPolicy(): Policy {
    return this.policy;
  }

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit() {
    for (const fn of this.listeners) {
      try {
        fn();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("[AccessRegistry] listener error", e);
      }
    }
  }
}

let _singleton: AccessRegistry | null = null;

export function getAccessRegistry(): AccessRegistry {
  if (!_singleton) _singleton = new AccessRegistry();
  return _singleton;
}
